import {
  Alert,
  Badge,
  BreadcrumbGroup,
  Button,
  Header,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './style.css';
import { Redirect, useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import {
  GET_CMS_CONTAINER,
  GET_CMS_CONTAINER_HISTORY,
  GET_CMS_LIST_SITES,
  GET_PARTS,
  GET_CYCLE_COUNT_HISTORY,
  GET_CHILD_CONTAINERS,
  VALIDATE_CONTAINER_OPERATION,
  MANAGE_CONTAINER_PERMISSIONS,
} from '../../../services/container-management/queries';
import { ContainerDetail, ContainerDetailProps } from './ContainerDetail';
import sizeOptions from '../mocks/size.json';
import sizeReasonCodes from '../mocks/sizeReasonCodes.json';
import deleteReasonCodes from '../mocks/deleteReasonCodes.json';
import rulesReasonCodes from '../mocks/rulesReasonCodes.json';
import { getFQContainerName, transformContainerForView } from './helper';
import {
  LocationInfo,
  PartsInfo,
  CycleCountHistoryInfo,
  ChildContainersInfo,
} from '../../../services/container-management/types';
import PartsDetail, { PartsOptions } from './PartsDetail';
import CycleCountHistoryDetail from './CycleCountHistoryDetail';
import ChildContainers from './ChildContainers';
import ContainerHistory, {
  ContainerHistoryDiff,
  convertContainerHistoryToDiff,
} from './ContainerHistory';
import ContainerDeleteConfirmationModal from './ContainerDeleteConfirmationModal';
import ContainerSeals from '../../../components/ContainerManagement/ContainerSeals';
import ContainerTickets from '../../../components/ContainerManagement/ContainerTickets';

export type ViewContainerProps = {
  containerId: string;
};

export default function ViewContainer(): ReactElement {
  const { t } = useTranslation();
  const { containerId } = useParams<ViewContainerProps>();
  const [data, setData] = useState<ContainerDetailProps>();
  const [isContainerDeletable, setIsContainerDeletable] = useState(false);
  const [managePermissions, setManagePermissions] = useState(false);
  const [locations, setLocations] = useState<Map<string, LocationInfo>>();
  const [parts, setParts] = useState<PartsInfo[]>();
  const [cycleCountHistory, setCycleCountHistory] = useState<CycleCountHistoryInfo>();
  const [childContainers, setChildContainers] = useState<ChildContainersInfo>();
  const [containerHistoryDiffs, setContainerHistoryDiffs] = useState<ContainerHistoryDiff[]>();
  const [fqContainerName, setFQContainerName] = useState('');
  const [siteName, setSiteName] = useState('');
  const [showContainerDeleteConfirmModal, setShowContainerDeleteConfirmModal] = useState(false);
  const [redirect, setRedirect] = useState<string>('');
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [totalParts, setTotalParts] = useState<number>(0);
  const [partsOptions, setPartsOptions] = useState<PartsOptions>({
    pageSize: 10,
  });

  const breadcrumbs = [
    {
      text: t('breadcrumb.home'),
      href: '/',
    },
    {
      text: t('breadcrumb.inventory-management'),
      href: '/assets-inventory/',
    },
    {
      text: t('breadcrumb.container-management'),
      href: '/assets-inventory/container',
    },
    {
      text: t('breadcrumb.container-details'),
      href: '',
    },
  ];

  const [getContainer, { loading: getContainerLoading, error: getContainerError }] = useLazyQuery(
    GET_CMS_CONTAINER,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        setData({
          ...transformContainerForView(result.getCmsContainer),

          // TODO add in the logic to determine if the container can be deleted
          isContainerEditable: true,
          isContainerDeletable: false,
          sizeOptions,
          sizeReasonCodes,
          rulesReasonCodes,
        });
      },
    }
  );

  const [validateContainerOperation, { loading: validateContainerOperationLoading }] = useLazyQuery(
    VALIDATE_CONTAINER_OPERATION,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        setIsContainerDeletable(result.validateContainerOperation.isValid);
      },
    }
  );

  // query manageContainerPermissions
  const [callManageContainerPermissions, { loading: manageContainerPermissionsLoading }] =
    useLazyQuery(MANAGE_CONTAINER_PERMISSIONS, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        setManagePermissions(result?.manageContainerPermissionsCheck?.user);
      },
    });

  const [listSites, { loading: listSitesLoading, error: listSitesError }] = useLazyQuery(
    GET_CMS_LIST_SITES,
    {
      variables: {},
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      onCompleted: (result) => {
        setLocations(result.listSites.locations);
      },
    }
  );

  const [callGetParts, { loading: isGetPartsLoading, error: getPartsError }] = useLazyQuery(
    GET_PARTS,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        setParts(result?.getParts?.parts);
        setTotalParts(result?.getParts?.totalResults);
      },
    }
  );

  const [
    callGetCycleCountHistory,
    { loading: isGetCycleCountHistoryLoading, error: getCycleCountHistoryError },
  ] = useLazyQuery(GET_CYCLE_COUNT_HISTORY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setCycleCountHistory(result?.getCycleCountHistory?.cycleCountHistory);
    },
  });

  const [
    callGetChildContainers,
    { loading: isChildContainersLoading, error: getChildContainersError },
  ] = useLazyQuery(GET_CHILD_CONTAINERS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setChildContainers(result?.getChildContainers);
    },
  });

  const [
    callGetContainerHistory,
    { loading: isGetContainerHistoryLoading, error: getContainerHistoryError },
  ] = useLazyQuery(GET_CMS_CONTAINER_HISTORY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setContainerHistoryDiffs(convertContainerHistoryToDiff(result?.getCmsContainerHistory));
    },
  });

  useEffect(() => {
    if ((pageIndex - 1) * partsOptions.pageSize >= totalParts) {
      setPageIndex(1);
    } else {
      callGetParts({
        variables: {
          containerId,
          containerFullName: fqContainerName,
          containerType: data?.getContainerResponse.containerDefinition.containerType,
          maxResults: partsOptions.pageSize,
          from: (pageIndex - 1) * partsOptions.pageSize,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, partsOptions]);

  useEffect(() => {
    getContainer({ variables: { containerId } });
    callManageContainerPermissions();
    listSites();
    // TODO support more than 2000 history records
    callGetCycleCountHistory({ variables: { containerId, maxResults: 2000 } });
    callGetChildContainers({ variables: { containerIdentifier: containerId, maxResults: 1000 } });
    // TODO support more than 1000 versions
    callGetContainerHistory({ variables: { containerId, start: 0, maxResults: 1000 } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      callGetParts({
        variables: {
          containerId,
          containerFullName: fqContainerName,
          containerType: data.getContainerResponse.containerDefinition.containerType,
          maxResults: partsOptions.pageSize,
          from: (pageIndex - 1) * partsOptions.pageSize,
        },
      });
      validateContainerOperation({ variables: { containerId, operation: 'DELETE' } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (data && locations) {
      const { container, location } = data.getContainerResponse;
      const siteId =
        location?.parentId ??
        container.parentlocation.find((i) => i.type === 'LAST_SITE')?.value ??
        '';

      let newSiteName = '';
      if (siteId) {
        Object.values(locations).forEach((loc: LocationInfo) => {
          if (loc.locationId === siteId) {
            newSiteName = loc.locationName;
            setSiteName(newSiteName);
          }
        });
      }
      setFQContainerName(
        getFQContainerName(
          container,
          data?.getContainerResponse?.containerDefinition,
          newSiteName,
          location?.locationName
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, locations]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  if (
    getContainerLoading ||
    manageContainerPermissionsLoading ||
    validateContainerOperationLoading ||
    listSitesLoading
  ) {
    return <Spinner size="big" />;
  }
  return (
    <div className="container-max-width">
      <SpaceBetween direction="vertical" size="l">
        {getContainerError && <Alert type="error">{getContainerError.message}</Alert>}
        {listSitesError && <Alert type="error">{listSitesError.message}</Alert>}
        {getCycleCountHistoryError && (
          <Alert type="error">{getCycleCountHistoryError.message}</Alert>
        )}
        {getChildContainersError && <Alert type="error">{getChildContainersError.message}</Alert>}
        {getContainerHistoryError && <Alert type="error">{getContainerHistoryError.message}</Alert>}
        {!getContainerError && data && (
          <>
            <BreadcrumbGroup items={breadcrumbs} />
            <Header
              data-test-id="page-header"
              variant="h1"
              actions={
                <SpaceBetween direction="horizontal" size="s">
                  <Button
                    data-test-id="button-delete-container"
                    loading={false}
                    disabled={!managePermissions || !isContainerDeletable}
                    variant="normal"
                    onClick={() => setShowContainerDeleteConfirmModal(true)}
                  >
                    {t('Delete Container')}
                  </Button>
                  <Button
                    data-test-id="button-back"
                    loading={false}
                    variant="primary"
                    onClick={() => setRedirect('/container/manage')}
                  >
                    {t('Search containers')}
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="s" direction="horizontal">
                {fqContainerName}
                <div className="vertical-align-info">
                  {parts?.length ? (
                    <Badge color="green">Contains parts</Badge>
                  ) : (
                    <Badge color="grey">Empty</Badge>
                  )}
                </div>
              </SpaceBetween>
            </Header>
            <ContainerDetail
              data={data}
              siteName={siteName}
              fqname={fqContainerName}
              managePermissions={managePermissions}
            />
            {data?.getContainerResponse.containerDefinition.metadataConfiguration?.sealIdRequired
              ?.default && (
              <ContainerSeals
                container={data.getContainerResponse.container}
                containerFullName={fqContainerName}
              />
            )}
            {data?.getContainerResponse.containerDefinition.metadataConfiguration?.ticketIdRequired
              ?.default && (
              <ContainerTickets
                container={data.getContainerResponse.container}
                containerFullName={fqContainerName}
              />
            )}
            {getPartsError && (
              <PartsDetail
                parts={[]}
                totalParts={totalParts}
                loadingParts={isGetPartsLoading}
                pageIndex={pageIndex}
                setPageIndex={setPageIndex}
                setPartsOptions={setPartsOptions}
              />
            )}
            {parts && (
              <PartsDetail
                parts={parts}
                totalParts={totalParts}
                loadingParts={isGetPartsLoading}
                pageIndex={pageIndex}
                setPageIndex={setPageIndex}
                setPartsOptions={setPartsOptions}
              />
            )}
            {cycleCountHistory && (
              <CycleCountHistoryDetail
                cycleCountHistory={cycleCountHistory}
                loadingCycleCountHistory={isGetCycleCountHistoryLoading}
              />
            )}
            {childContainers && (
              <ChildContainers
                childContainers={childContainers}
                loadingChildContainers={isChildContainersLoading}
              />
            )}
            {containerHistoryDiffs && (
              <ContainerHistory
                data={containerHistoryDiffs}
                loading={isGetContainerHistoryLoading}
              />
            )}
            {showContainerDeleteConfirmModal && (
              <ContainerDeleteConfirmationModal
                containerId={data.getContainerResponse.container.containerId}
                containerName={fqContainerName}
                version={data.getContainerResponse.container.version}
                deleteReasonCodes={deleteReasonCodes}
                showContainerDeleteConfirmModal
                onClose={() => setShowContainerDeleteConfirmModal(false)}
              />
            )}
          </>
        )}
      </SpaceBetween>
    </div>
  );
}
